import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { FiEdit, FiTrash2, FiPlus, FiSearch } from 'react-icons/fi';
import Swal from 'sweetalert2';
import ConditionalButton from '../components/common/ConditionalButton';
import ConditionalActions from '../components/common/ConditionalActions';

const QualificationsPage = () => {
  const [qualifs, setQualifs] = useState([]);
  const [matieres, setMatieres] = useState([]);
  const [niveaux, setNiveaux] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ matiere_id: '', niveauqualif_id: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [filtered, setFiltered] = useState([]);

  const fetchAll = async () => {
    const [qRes, mRes, nRes] = await Promise.all([
      api.get('/qualifications'),
      api.get('/matieres'),
      api.get('/niveau-qualif-prof'),
    ]);
    setQualifs(qRes.data.data || []);
    setMatieres(mRes.data.data || []);
    setNiveaux(nRes.data.data || []);
  };

  useEffect(() => { fetchAll(); }, []);

  useEffect(() => {
    setFiltered(
      qualifs.filter(q => {
        const mat = matieres.find(m => m.id === q.matiere_id) || {};
        const niv = niveaux.find(n => n.id === q.niveauqualif_id) || {};
        const term = searchTerm.toLowerCase();
        return (
          (mat.lib_matiere || '').toLowerCase().includes(term) ||
          (niv.niveau || '').toLowerCase().includes(term)
        );
      })
    );
  }, [searchTerm, qualifs, matieres, niveaux]);

  const handleEdit = (qualif) => {
    setEditing(qualif);
    setForm({
      matiere_id: qualif.matiere_id || '',
      niveauqualif_id: qualif.niveauqualif_id || ''
    });
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Supprimer cette qualification ?',
      text: 'Cette action est irréversible.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler',
    });
    if (result.isConfirmed) {
      await api.delete(`/qualifications/${id}`);
      fetchAll();
      Swal.fire('Supprimé !', 'La qualification a été supprimée.', 'success');
    }
  };

  const handleModalSubmit = async (e) => {
    e.preventDefault();
    if (editing) {
      await api.put(`/qualifications/${editing.id}`, form);
    } else {
      await api.post('/qualifications', form);
    }
    setModalOpen(false);
    setEditing(null);
    setForm({ matiere_id: '', niveauqualif_id: '' });
    fetchAll();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold dark:text-gray-100">Gestion des qualifications</h1>
        <ConditionalButton
          entity="qualifications"
          permission="create"
          className="px-3 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 flex items-center"
          onClick={() => { setEditing(null); setForm({ matiere_id: '', niveauqualif_id: '' }); setModalOpen(true); }}
          title="Ajouter une qualification"
        >
          <FiPlus />
        </ConditionalButton>
      </div>
      {/* Search Bar */}
      <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow mb-6">
        <div className="relative">
          <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" />
          <input
            type="text"
            placeholder="Rechercher une qualification..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
      </div>
      <div className="overflow-x-auto rounded shadow">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
          <thead className="bg-gray-200 dark:bg-gray-700">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-bold uppercase text-gray-900 dark:text-gray-200">Matière</th>
              <th className="px-4 py-2 text-left text-xs font-bold uppercase text-gray-900 dark:text-gray-200">Niveau</th>
              <th className="px-4 py-2 text-right text-xs font-bold uppercase text-gray-900 dark:text-gray-200">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {filtered.map((qualif) => {
              const mat = matieres.find(m => m.id === qualif.matiere_id) || {};
              const niv = niveaux.find(n => n.id === qualif.niveauqualif_id) || {};
              return (
                <tr key={qualif.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/80 transition-colors duration-150">
                  <td className="px-4 py-2 whitespace-nowrap text-gray-900 dark:text-gray-200">{mat.lib_matiere}</td>
                  <td className="px-4 py-2 whitespace-nowrap text-gray-900 dark:text-gray-200">{niv.niveau}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <ConditionalActions
                      entity="qualifications"
                      onEdit={() => handleEdit(qualif)}
                      onDelete={() => handleDelete(qualif.id)}
                      editIcon={FiEdit}
                      deleteIcon={FiTrash2}
                      className="flex space-x-2"
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {modalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 dark:bg-opacity-50 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 w-full max-w-md">
            <h2 className="text-lg font-semibold mb-4 dark:text-gray-100">{editing ? 'Modifier' : 'Ajouter'} une qualification</h2>
            <form onSubmit={handleModalSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1 dark:text-gray-200">Matière</label>
                <select value={form.matiere_id} onChange={e => setForm({ ...form, matiere_id: e.target.value })} className="w-full border dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500" required>
                  <option value="">-- Sélectionner --</option>
                  {matieres.map(m => (
                    <option key={m.id} value={m.id}>{m.lib_matiere}</option>
                  ))}
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1 dark:text-gray-200">Niveau</label>
                <select value={form.niveauqualif_id} onChange={e => setForm({ ...form, niveauqualif_id: e.target.value })} className="w-full border dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500" required>
                  <option value="">-- Sélectionner --</option>
                  {niveaux.map(n => (
                    <option key={n.id} value={n.id}>{n.niveau}</option>
                  ))}
                </select>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <button type="button" onClick={() => setModalOpen(false)} className="px-4 py-2 rounded bg-gray-200 dark:bg-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600">Annuler</button>
                <ConditionalButton
                  entity="qualifications"
                  permission={editing ? "update" : "create"}
                  type="submit"
                  className="px-4 py-2 rounded bg-primary-600 text-white hover:bg-primary-700"
                >
                  Enregistrer
                </ConditionalButton>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default QualificationsPage;